import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuction, useBuyNow } from '@/hooks/useAuctions';
import { useMe } from '@/hooks/useUser';
import { formatMoney, formatCondition, isAuctionTradingOpen } from '@/lib/format';
import { useBackNavigation } from '@/hooks/useBackNavigation';
import { useAuctionSocket } from '@/hooks/useAuctionSocket';


import { Gallery } from '@/components/auction/Gallery';
import { BigTimer } from '@/components/auction/BigTimer';
import { BidsList } from '@/components/auction/BidsList';
import { SellerCard } from '@/components/auction/SellerCard';
import { BidForm } from '@/components/auction/BidForm';
import { AutoBidSheet } from '@/components/auction/AutoBidSheet';
import { DealPanel } from '@/components/auction/DealPanel';

import { Tag } from '@/components/ui/Tag';
import { Skeleton } from '@/components/ui/Skeleton';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/common/EmptyState';
import { ReviewPanel } from '@/components/auction/ReviewPanel';

import { haptic } from '@/lib/telegram';
import styles from './AuctionPage.module.css';

export const AuctionPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const goBack = useBackNavigation();
  const { data: auction, isLoading, isError } = useAuction(id);
  useAuctionSocket(id);

  const { data: me } = useMe();
  const buyNow = useBuyNow(id ?? '');
  const [confirmBuyNow, setConfirmBuyNow] = useState(false);
  const [autoBidOpen, setAutoBidOpen] = useState(false);

  // Loading
  if (isLoading) {
    return (
      <div className={styles.page}>
        <Skeleton width="100%" height={380} rounded="sm" />
        <div className={styles.body}>
          <Skeleton width="80%" height={28} />
          <Skeleton width="50%" height={20} />
          <Skeleton width="100%" height={80} />
        </div>
      </div>
    );
  }

  if (isError || !auction) {
    return (
      <div className={styles.page}>
        <BackButton onBack={goBack} />
        <EmptyState
          title="Аукцион не найден"
          description="Возможно, лот был удалён или ссылка устарела"
          action={<Link to="/"><Button>На главную</Button></Link>}
        />
      </div>
    );
  }

  const canTrade = isAuctionTradingOpen(auction);
  const isOver = !canTrade;
  // Текущий пользователь — продавец этого лота: покупательских кнопок не показываем
  const isSeller = me?.id === auction.seller.id;

  return (
    <div className={styles.page}>
      <BackButton onBack={goBack} />

      {/* Галерея */}
      <Gallery photos={auction.photos} />

      {/* Отклонённый лот — владельцу: причина + редактирование */}
      {auction.status === 'rejected' && me && auction.seller.id === me.id && (
          <div className={styles.rejectedBlock}>
            <p className={styles.rejectedTitle}>Лот отклонён</p>
            {auction.rejected_reason && (
              <p className={styles.rejectedReason}>{auction.rejected_reason}</p>
            )}
            <Button
              variant="primary"
              fullWidth
              onClick={() => { haptic.medium(); navigate(`/edit/${auction.id}`); }}
            >
              Редактировать и отправить заново
            </Button>
          </div>
        )}

      {/* Тело */}
      <div className={styles.body}>
        {/* Теги + город */}
        <div className={styles.metaTop}>
          <div className={styles.tags}>
            {auction.tags.map((t) => (
              <Tag key={t.id} variant="accent">{t.name_ru}</Tag>
            ))}
          </div>
          <span className={styles.city}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            {auction.city}
          </span>
        </div>

        <h1 className={styles.title}>{auction.title}</h1>

        {/* Состояние */}
        <div className={styles.condition}>
          <span className={styles.conditionLabel}>Состояние</span>
          <span className={styles.conditionValue}>{formatCondition(auction.condition)}</span>
        </div>

        {/* Главный блок: цена + таймер */}
        <div className={styles.priceTimerGrid}>
          <div className={styles.priceCard}>
            <p className={styles.priceLabel}>Текущая ставка</p>
            <p className={styles.priceValue}>{formatMoney(auction.current_price)}</p>
            <p className={styles.priceMeta}>
              {auction.bids_count} ставок · шаг {formatMoney(auction.min_step)}
            </p>
          </div>

          <BigTimer auctionId={auction.id} endsAt={auction.ends_at} status={auction.status} />
        </div>

      {/* Buy now (если есть) — не показываем продавцу его же лота.
          Также прячем, если текущая ставка догнала/превысила цену выкупа:
          выкупать дороже, чем уже стоит лот, бессмысленно. */}
      {auction.buy_now_price &&
        auction.current_price < auction.buy_now_price &&
        !isOver &&
        !isSeller && (
          <>
            <button
              className={styles.buyNowBlock}
              onClick={() => { haptic.medium(); setConfirmBuyNow(true); }}
            >
              <div className={styles.buyNowLeft}>
                <p className={styles.buyNowLabel}>Купить сразу</p>
                <p className={styles.buyNowPrice}>{formatMoney(auction.buy_now_price)}</p>
              </div>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </button>

            {confirmBuyNow && (
              <div className={styles.buyNowConfirm}>
                <p className={styles.buyNowConfirmText}>
                  Купить лот сразу за {formatMoney(auction.buy_now_price)}? Аукцион
                  завершится немедленно, ты станешь победителем.
                </p>
                {buyNow.isError && (
                  <p className={styles.buyNowError}>
                    {(buyNow.error as Error)?.message ?? 'Не удалось выкупить лот'}
                  </p>
                )}
                <div className={styles.buyNowConfirmActions}>
                  <Button
                    variant="secondary"
                    onClick={() => { haptic.light(); setConfirmBuyNow(false); }}
                  >
                    Отмена
                  </Button>
                  <Button
                    variant="primary"
                    loading={buyNow.isPending}
                    onClick={() => {
                      haptic.medium();
                      buyNow.mutate(undefined, {
                        onSuccess: () => setConfirmBuyNow(false),
                      });
                    }}
                  >
                    Купить за {formatMoney(auction.buy_now_price)}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Продавец */}
        <Section title="Продавец">
          <SellerCard seller={auction.seller_extended} />
        </Section>

        {/* Описание */}
        <Section title="Описание">
          <p className={styles.description}>{auction.description}</p>
        </Section>

        {/* История ставок */}
        <Section title={`История ставок · ${auction.bids_count}`}>
          <BidsList auctionId={auction.id} currentLeaderId={auction.current_leader_id} />
        </Section>

        {/* Панель сделки (завершённый лот, участникам) */}
        {me && <DealPanel auction={auction} myUserId={me.id} />}

        {/* Панель отзыва (проданный лот, участникам) */}
        {me && <ReviewPanel auction={auction} myUserId={me.id} />}

        {/* Резерв для sticky-блока внизу (только когда форма ставки показана) */}
        {!isOver && !isSeller && <div className={styles.bottomReserve} />}
      </div>

      {/* Sticky CTA внизу — не показываем продавцу его же лота */}
      {!isOver && !isSeller && (
        <div className={styles.stickyBottom}>
          <BidForm auction={auction} onOpenAutoBid={() => setAutoBidOpen(true)} />
        </div>
      )}

      <AutoBidSheet
        isOpen={autoBidOpen}
        onClose={() => setAutoBidOpen(false)}
        auction={auction}
      />
    </div>
  );
};

// ── Вспомогательные ───────────────────────────────────

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className={styles.section}>
    <p className={styles.sectionTitle}>{title}</p>
    {children}
  </section>
);

const BackButton = ({ onBack }: { onBack: () => void }) => (
  <button
    className={styles.backBtn}
    onClick={() => { haptic.light(); onBack(); }}
    aria-label="Назад"
  >
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="m15 18-6-6 6-6" />
    </svg>
  </button>
);